// Shared wire types between api/ and web/. Keep this file dependency-free.

export type EnvName = 'prod' | 'test';

export interface Me {
  sub: string;
  role: 'operator' | 'viewer';
  mfa: boolean;
  email: string | null;
}

export interface EnvSummary {
  name: EnvName;
  label: string;
  account: string;
  region: string;
}

/** One section of an overview: either data or a reason it could not be read. */
export type Section<T> = { ok: true; data: T } | { ok: false; error: string };

export interface EndpointHealth {
  id: string;
  label: string;
  url: string;
  ok: boolean;
  status: number | null;
  ms: number;
  note: string;
  /** build stamp the server compiles in (2026-10-13 release); null where the body has none */
  buildSha: string | null;
  buildTime: string | null;
}

export interface LambdaIdentity {
  id: string;
  fn: string;
  label: string;
  owner: 'server' | 'gui';
  codeSha256: string;
  lastModified: string;
  runtime: string;
  memoryMb: number;
  timeoutS: number;
  architectures: string[];
  state: string;
  lastUpdateStatus: string;
  reservedConcurrency: number | null;
  envKeys: string[];
  /** sha256 prefix of each env value; lets the UI diff drift without ever seeing a value */
  envDigest: Record<string, string>;
}

export type MetricWindow = '1h' | '24h' | '7d';

export interface MetricSeries {
  id: string;
  label: string;
  group: string;
  unit: string;
  points: Array<[number, number]>;
  last: number | null;
  sum: number;
  max: number | null;
}

export interface Metrics {
  window: MetricWindow;
  start: string;
  end: string;
  periodS: number;
  series: Record<string, MetricSeries>;
}

export interface OperatorAction {
  id: string;
  at: string;
  by: string;
  env: EnvName | 'all';
  kind: string;
  target: string;
  result: 'ok' | 'failed';
  detail: string | null;
}

export interface Overview extends OverviewExtras {
  env: EnvSummary;
  asOf: string;
  health: Section<EndpointHealth[]>;
  lambdas: Section<LambdaIdentity[]>;
  metrics: Section<Metrics>;
  actions: Section<OperatorAction[]>;
}

// ── Phase 1 ──────────────────────────────────────────────────────────────

export interface CanaryResult { ok: boolean; status: number | null; ms: number }
export interface CanaryPoint { at: string; results: Record<string, CanaryResult> }
export interface CanaryHistory {
  window: MetricWindow;
  points: CanaryPoint[];
  uptime: Record<string, { label: string; checks: number; failures: number; medianMs: number | null }>;
}

export interface AlarmAck { by: string; at: string; note: string | null }
export interface AlarmInfo {
  name: string;
  state: 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';
  reason: string;
  since: string;
  description: string | null;
  metric: string;
  ack: AlarmAck | null;
}

export type TimelineKind = 'deploy' | 'config-drift' | 'note' | 'reset';
export interface TimelineEvent {
  id: string;
  at: string;
  env: EnvName | 'all';
  kind: TimelineKind;
  title: string;
  detail: string | null;
  by: string | null;
}

export interface LogQuerySpec { id: string; label: string; description: string; kind: 'list' | 'stats'; needsText?: boolean }
export interface LogQueryResult {
  query: LogQuerySpec;
  queryText: string;
  window: MetricWindow;
  logGroups: string[];
  fields: string[];
  rows: Array<Record<string, string>>;
  stats: { recordsMatched: number; recordsScanned: number; bytesScanned: number };
  status: string;
  consoleUrl: string;
  asOf: string;
}

export type PostureState = 'ok' | 'warn' | 'bad' | 'unknown';
export interface PostureCheck { id: string; label: string; state: PostureState; detail: string; link?: string }
export interface Posture { env: EnvSummary; asOf: string; checks: PostureCheck[] }

export interface StackInfo { name: string; status: string; lastUpdated: string | null; description: string | null }
export interface Deploys {
  env: EnvSummary;
  asOf: string;
  /** health probes, for the build stamps the server and auth Lambdas report */
  health: Section<EndpointHealth[]>;
  lambdas: Section<LambdaIdentity[]>;
  stacks: Section<StackInfo[]>;
  history: Section<TimelineEvent[]>;
}

export interface OverviewExtras {
  alarms: Section<AlarmInfo[]>;
  timeline: Section<TimelineEvent[]>;
  canaries: Section<CanaryHistory>;
  samples: Section<Samples>;
}

// ── Phase 2: tenants ─────────────────────────────────────────────────────

/** Returned with HTTP 503 while the read-only database role is not live. */
export const DB_UNAVAILABLE = 'db_unavailable:';

export interface Counts {
  users: number; studios: number; projects: number; machines: number;
  invitesPending: number; packages: number; cutOff: number;
}
export interface CountSample { at: string; counts: Counts; migrations: number }
export interface Samples {
  latest: CountSample | null;
  /** per count, [ts, value] over the window */
  series: Partial<Record<keyof Counts, Array<[number, number]>>>;
}

export interface ClerkUserInfo {
  id: string;
  email: string | null;
  createdAt: string | null;
  lastSignInAt: string | null;
  lastActiveAt: string | null;
  banned: boolean;
  locked: boolean;
  mfa: boolean;
}

export interface Person {
  /** projector user id; null when the Clerk user never registered */
  userId: string | null;
  authId: string;
  email: string | null;
  isProjectorDev: boolean;
  memberships: number;
  ownedStudios: number;
  cutOffAt: string | null;
  cutOffReason: string | null;
  lastClerkCheckAt: string | null;
  clerk: ClerkUserInfo | null;
}

export interface Membership {
  studioId: string; studioLabel: string; studioName: string; isOwner: boolean;
  studioUserId: string; studioRoles: string[]; projectGrants: number;
}
/** The People list. `dbUnavailable` means the Projector columns are unknown, not empty. */
export interface PeopleResponse { rows: Person[]; dbUnavailable: boolean }
export interface PersonDetail { person: Person; memberships: Membership[]; sessions: Array<{ id: string; lastActiveAt: string | null; status: string }>; dbUnavailable: boolean }

export interface StudioRow {
  id: string; studioName: string; label: string; ownerId: string | null; ownerEmail: string | null;
  members: number; projects: number; machines: number; invitesPending: number; invitesExpired: number;
  packages: number; packageBytes: number; ownerIsMember: boolean;
}
export interface RosterRow { studioUserId: string; userId: string | null; email: string | null; authId: string | null; isOwner: boolean; studioRoles: string[]; projectRoles: string[] }
export interface ProjectRow { id: string; name: string; label: string; clientId: string | null; grants: number }
export interface MachineRow { id: string; label: string; studioId: string; studioLabel: string; roles: number }
/** One enrolment of a physical machine in one studio. */
export interface MachineEnrolment { studioMachineId: string; studioId: string; studioLabel: string; label: string; roles: number }
/**
 * A physical machine, grouped across studios by an opaque key: a one-way digest
 * of the fingerprint computed inside Postgres. The fingerprint itself never
 * leaves the database and the key is never shown.
 */
export interface PhysicalMachine { key: string; name: string; labels: string[]; enrolments: MachineEnrolment[] }
export interface InviteRow { id: string; targetEmail: string; expiration: string; hasClerkInvitation: boolean; pending: boolean }
export interface StudioDetail {
  studio: StudioRow; roster: RosterRow[]; projects: ProjectRow[]; machines: MachineRow[]; invites: InviteRow[];
  config: Array<{ key: string; value: string }>;
}

export interface PackageRow {
  id: string; packageName: string; displayName: string; description: string; version: string; author: string; scope: string;
  studioId: string | null; studioLabel: string | null; archiveSize: number; archiveHash: string; publishedAt: string; unpublishedAt: string | null;
  /** manifest category: dcc | folder_structure | template | … | null */
  category: string | null;
  tags: string[];
  dependencies: number;
}

export interface MigrationRow { version: string; appliedAt: string }

export interface ClerkInvitation { id: string; email: string; status: string; createdAt: string | null; expiresAt: string | null }
